import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { STATUS } from 'react-joyride'
import { pageFeatureTours } from './releases'
import { ReleaseTour } from './ReleaseTour'
import { hasSeenRelease, markReleaseSeen } from './releaseHistory'
import { DetectedFeatureTour, discoverFeatureTours, isFeatureTourTargetVisible } from './featureTourAttributes'

interface PageFeatureTourProps {
    historyScope: string
    enabled: boolean
    onActiveChange: (active: boolean) => void
}

export const PageFeatureTour = ({ enabled, onActiveChange, historyScope }: PageFeatureTourProps) => {
    const { pathname } = useLocation()
    const seen = useRef(new Set<string>())
    const [active, setActive] = useState<{ pathname: string; tour: DetectedFeatureTour } | null>(null)

    useEffect(() => {
        if (!enabled || active) return
        const check = () => {
            // The static registry is retained for existing introductions; new pages can use attributes alone.
            const configured: DetectedFeatureTour[] = pageFeatureTours
                .filter((tour) => tour.route === pathname)
                .map((tour) => ({
                    id: tour.id,
                    steps: (tour.waitForEnabledTargets &&
                    !tour.steps.every((step) => {
                        const target = document.querySelector(step.target)
                        return target !== null && isFeatureTourTargetVisible(target)
                    })
                        ? []
                        : tour.steps
                    )
                        .filter((step) => {
                            const target = document.querySelector(step.target)
                            return target !== null && isFeatureTourTargetVisible(target)
                        })
                        .map((step) => ({ ...step, disableBeacon: true, placement: 'auto' })),
                }))
            const tour = [...configured, ...discoverFeatureTours()].find(
                (candidate) => candidate.steps.length > 0 && !seen.current.has(candidate.id) && !hasSeenRelease(historyScope, candidate.id, 'minor')
            )
            if (!tour) return
            seen.current.add(tour.id)
            setActive({ pathname, tour })
            onActiveChange(true)
        }
        let timer: ReturnType<typeof setTimeout> | undefined
        const observer = new MutationObserver(() => {
            if (timer !== undefined) return
            timer = setTimeout(() => {
                timer = undefined
                check()
            }, 50)
        })
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: [
                'data-feature-tour',
                'data-feature-tour-release',
                'data-feature-tour-title',
                'data-feature-tour-description',
                'data-feature-tour-order',
                'hidden',
                'aria-hidden',
                'disabled',
                'aria-disabled',
                'class',
                'style',
            ],
        })
        check()
        return () => {
            observer.disconnect()
            clearTimeout(timer)
        }
    }, [enabled, active, pathname, historyScope, onActiveChange])

    useEffect(() => {
        if (!enabled || (active && active.pathname !== pathname)) {
            setActive(null)
            onActiveChange(false)
        }
    }, [enabled, active, pathname, onActiveChange])

    if (!enabled || !active || active.pathname !== pathname) return null
    return (
        <ReleaseTour
            key={active.tour.id}
            steps={active.tour.steps}
            onCallback={({ status }) => {
                if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
                    markReleaseSeen(historyScope, active.tour.id, 'minor')
                    setActive(null)
                    onActiveChange(false)
                }
            }}
        />
    )
}
